import { useState } from "react";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { useEscapeKey } from "../../hooks/useEscapeKey";
import { useThemeContext } from "../../theme/ThemeContext";
import { overlayBackdrop } from "../../theme/styles";
import { RADIUS } from "../../theme/tokens";
import { Button } from "../shared";

function todayISO() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" });
}

export function ChangeDateModal({ currentDate, onConfirm, onClose }) {
  const { SA, GLASS, TYPOGRAPHY } = useThemeContext();
  useLockBodyScroll();
  useEscapeKey(onClose);
  const [newDate, setNewDate] = useState(currentDate);

  // Allowed range: 7 days back to 1 day forward
  const laxDate = (offsetDays = 0) => {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    return d.toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" });
  };

  // Build the list of selectable dates for the button grid
  const dates = [];
  for (let i = -7; i <= 1; i++) dates.push(laxDate(i));

  const dayLabel = (iso) => {
    const d = new Date(iso + "T12:00:00");
    const today = todayISO();
    const tomorrow  = (() => { const t = new Date(); t.setDate(t.getDate()+1); return t.toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" }); })();
    const yesterday = (() => { const t = new Date(); t.setDate(t.getDate()-1); return t.toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" }); })();
    if (iso === today)     return "Today";
    if (iso === tomorrow)  return "Tomorrow";
    if (iso === yesterday) return "Yesterday";
    return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
  };

  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }} style={{ ...overlayBackdrop, zIndex: 200,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...GLASS.modal,
        borderRadius: 16, width: "100%", maxWidth: 360, boxShadow: "0 24px 80px rgba(0,0,0,0.9)",
        display: "flex", flexDirection: "column" }}>

        {/* Header */}
        <div style={{ padding: "18px 20px 14px", borderBottom: `1px solid ${SA.borderGray}`,
          display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: TYPOGRAPHY.caption, color: SA.lightSilver, letterSpacing: "0.2em", textTransform: "uppercase",
            fontFamily: "'Helvetica Neue',Arial,sans-serif", fontWeight: 600 }}>Select Date</span>
          <Button variant="icon" onClick={onClose}
            style={{ background: SA.medGray, color: SA.silver, width: 28, height: 28, fontSize: 16 }}>×</Button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {dates.slice().reverse().map(iso => {
              const isSelected = iso === newDate;
              const isToday = iso === todayISO();
              return (
                <Button key={iso} onClick={() => setNewDate(iso)}
                  style={{ padding: "11px 16px", borderRadius: 2, textAlign: "left",
                    background: isSelected ? SA.medGray : "transparent",
                    border: `1px solid ${isSelected ? SA.lightSilver : SA.borderGray}`,
                    color: isSelected ? SA.white : isToday ? SA.lightSilver : SA.silver,
                    fontFamily: "'Helvetica Neue',Arial,sans-serif",
                    justifyContent: "space-between" }}>
                  <span style={{ fontSize: TYPOGRAPHY.caption, letterSpacing: "0.1em" }}>{dayLabel(iso)}</span>
                  <span style={{ fontSize: TYPOGRAPHY.tiny, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", letterSpacing: "0.08em",
                    color: isSelected ? SA.lightSilver : SA.borderGray }}>{iso}</span>
                </Button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 20px 16px", borderTop: `1px solid ${SA.borderGray}` }}>
          <Button fullWidth
            onClick={() => { if (newDate !== currentDate) onConfirm(newDate); else onClose(); }}
            style={{ padding: "13px", borderRadius: 2,
              background: newDate !== currentDate ? SA.lightSilver : SA.medGray,
              border: "none", color: SA.black, fontSize: TYPOGRAPHY.label, letterSpacing: "0.18em",
              textTransform: "uppercase", fontFamily: "'Helvetica Neue',Arial,sans-serif", fontWeight: 600 }}>
            {newDate !== currentDate ? `Go to ${dayLabel(newDate)}` : "Already on this date"}
          </Button>
        </div>
      </div>
    </div>
  );
}
